import {
    DestinationEvent,
    DestinationResponse,
    DiscoveryEvent,
    FormattedEventResponse,
    transformDestinationEvent,
} from '@eb/event-renderer';
import { sdkRequest, translateServerErrors } from '@eb/http';
import { gettext } from '@eb/i18n';
import isEmpty from 'lodash/isEmpty';

interface PaginationResponse {
    object_count: number;
    page_number: number;
    page_size: number;
    page_count: number;
    has_more_items: boolean;
}

interface DiscoveryResponse {
    events: Array<DiscoveryEvent>;
    pagination: PaginationResponse;
}

const STANDARD_SERVER_ERROR = gettext('An error occured.');

const GENERAL_ERROR_SPEC = {
    default: () => STANDARD_SERVER_ERROR,
};

const _getOrganizerEventsApiUrl = (organizerId: string): string =>
    `/organizers/${organizerId}/events/`;

const _getOrganizerDestinationEventsApiUrl = (eventIds: string): string =>
    `/destination/events/?event_ids=${eventIds}&expand=event_sales_status,image,primary_venue,saves,series,ticket_availability,primary_organizer&page_size=3&include_parent_events=true&status=live`;

const getOrganizerEventsFromDiscovery = (
    organizerId: string,
): Promise<DiscoveryResponse> => {
    return sdkRequest(_getOrganizerEventsApiUrl(organizerId))
        .then((response: DiscoveryResponse) => response)
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};

const getOrganizerEventIds = async (
    organizerId: string,
): Promise<Array<string>> => {
    const { events } = await getOrganizerEventsFromDiscovery(organizerId);
    return events.map((event: DiscoveryEvent) => event.id);
};

const getRelatedEventIds = (eventIds: string[], eventIdOwner: string) =>
    eventIds.filter((eventId) => eventId !== eventIdOwner);

const getOpenSaleEvents = (events: DestinationEvent[]): DestinationEvent[] =>
    events.filter(
        (event: DestinationEvent) => !event.ticket_availability?.is_sold_out,
    );

export const getOrganizerEventsFromDestination = async (
    organizerId: string,
    eventId: string,
): Promise<FormattedEventResponse | undefined> => {
    const allEventIds = await getOrganizerEventIds(organizerId);
    const relatedEventIds = getRelatedEventIds(allEventIds, eventId);

    if (isEmpty(relatedEventIds)) {
        return;
    }

    return sdkRequest(
        _getOrganizerDestinationEventsApiUrl(relatedEventIds.join()),
    )
        .then((response: DestinationResponse) => {
            const openSaleEvents = getOpenSaleEvents(response.events);
            return {
                events: openSaleEvents.map((event: DestinationEvent) =>
                    transformDestinationEvent(event),
                ),
            };
        })
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};
